'use client';

import { useRef } from 'react';
import { PdfExperienceContentSchema, type TPdfExperienceContent } from '@shared/schemas';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
	FileText,
	Building2,
	Upload,
	Trash2,
	Check,
	ExternalLink,
	Zap,
	FileUp,
} from 'lucide-react';

type FormValue = Partial<TPdfExperienceContent>;

interface Props {
	value: FormValue;
	onChange: (value: FormValue) => void;
	onValidityChange: (valid: boolean) => void;
}

const COLOR_PALETTES = [
	{ name: 'Royal Blue', hex: '#1d4ed8' },
	{ name: 'Ruby Red', hex: '#e11d48' },
	{ name: 'Forest Emerald', hex: '#059669' },
	{ name: 'Indigo Purple', hex: '#6366f1' },
	{ name: 'Charcoal Slate', hex: '#0f172a' },
	{ name: 'Amber Bronze', hex: '#d97706' },
];

function formatBytes(bytes: number): string {
	if (bytes === 0) return '0 Bytes';
	const k = 1024;
	const sizes = ['Bytes', 'KB', 'MB', 'GB'];
	const i = Math.floor(Math.log(bytes) / Math.log(k));
	return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

export function PdfExperienceForm({ value, onChange, onValidityChange }: Props) {
	const coverInputRef = useRef<HTMLInputElement>(null);
	const pdfDocInputRef = useRef<HTMLInputElement>(null);

	const isDirectPdf = Boolean(value.directPdf);

	const updateFields = (patch: Partial<TPdfExperienceContent>) => {
		const updated = {
			type: 'pdf' as const,
			title: value.title ?? '',
			pdfUrl: value.pdfUrl ?? '',
			directPdf: value.directPdf ?? false,
			accentColor: value.accentColor ?? '#1d4ed8',
			...value,
			...patch,
		};
		onChange(updated);
		onValidityChange(PdfExperienceContentSchema.safeParse(updated).success);
	};

	const handleCoverUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;
		if (file.size > 1_000_000) {
			alert('Cover image must be under 1 MB.');
			return;
		}
		const reader = new FileReader();
		reader.onload = () => {
			if (typeof reader.result === 'string') {
				updateFields({ coverUrl: reader.result });
			}
		};
		reader.readAsDataURL(file);
	};

	const handlePdfFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;
		if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
			alert('Please select a valid PDF document (.pdf).');
			return;
		}
		if (file.size > 1_800_000) {
			alert('Uploaded PDF file must be 1.8 MB or smaller.');
			return;
		}
		const reader = new FileReader();
		reader.onload = () => {
			if (typeof reader.result === 'string') {
				const autoTitle = value.title || file.name.replace(/\.[^/.]+$/, '');
				updateFields({
					pdfUrl: reader.result,
					fileSize: formatBytes(file.size),
					title: autoTitle,
				});
			}
		};
		reader.readAsDataURL(file);
	};

	const testPdfLink = () => {
		if (value.pdfUrl && value.pdfUrl.startsWith('https://')) {
			window.open(value.pdfUrl, '_blank', 'noopener,noreferrer');
		} else if (value.pdfUrl && value.pdfUrl.startsWith('data:application/pdf')) {
			try {
				const base64Index = value.pdfUrl.indexOf('base64,');
				const base64Data = base64Index !== -1 ? value.pdfUrl.slice(base64Index + 7) : '';
				const byteCharacters = atob(base64Data);
				const byteNumbers = new Array(byteCharacters.length);
				for (let i = 0; i < byteCharacters.length; i++) {
					byteNumbers[i] = byteCharacters.charCodeAt(i);
				}
				const byteArray = new Uint8Array(byteNumbers);
				const blob = new Blob([byteArray], { type: 'application/pdf' });
				const blobUrl = URL.createObjectURL(blob);
				window.open(blobUrl, '_blank', 'noopener,noreferrer');
				setTimeout(() => URL.revokeObjectURL(blobUrl), 30_000);
			} catch (e) {
				console.error('Failed to open PDF preview:', e);
				alert('Could not open PDF preview.');
			}
		} else {
			alert('Please provide or upload a PDF first.');
		}
	};

	const isUploadedPdf = Boolean(value.pdfUrl?.startsWith('data:application/pdf'));

	return (
		<div className="space-y-6">
			{/* Primary PDF Source (Direct Upload or HTTPS Link) */}
			<div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 shadow-xs space-y-5">
				<div className="flex items-center gap-3 border-b border-slate-100 pb-3">
					<div className="flex size-9 items-center justify-center rounded-xl bg-slate-900 text-white shadow-xs">
						<FileText className="size-5" />
					</div>
					<div>
						<h2 className="text-base font-bold text-slate-900">PDF File Source</h2>
						<p className="text-xs text-slate-500">
							Upload your PDF directly from your device or provide a direct HTTPS link
						</p>
					</div>
				</div>

				{/* Upload PDF Directly */}
				<div className="space-y-3">
					<Label className="text-xs font-semibold text-slate-700 block">
						Option A: Upload PDF Directly from device
					</Label>
					<div
						onClick={() => pdfDocInputRef.current?.click()}
						className={`relative flex flex-col items-center justify-center p-5 rounded-2xl border-2 border-dashed transition cursor-pointer text-center ${
							isUploadedPdf
								? 'border-emerald-500 bg-emerald-50/40'
								: 'border-slate-300 bg-slate-50/60 hover:border-slate-900 hover:bg-slate-100/50'
						}`}
					>
						<input
							ref={pdfDocInputRef}
							type="file"
							accept="application/pdf,.pdf"
							onChange={handlePdfFileUpload}
							className="hidden"
						/>

						{isUploadedPdf ? (
							<div className="flex items-center gap-3">
								<div className="size-11 rounded-xl bg-emerald-600 text-white flex items-center justify-center shadow-xs shrink-0">
									<Check className="size-5 stroke-[2.5]" />
								</div>
								<div className="text-left">
									<p className="text-sm font-bold text-slate-900">PDF Uploaded Successfully</p>
									<p className="text-xs text-slate-500">
										{value.fileSize || 'PDF Document ready'} • Click to change file
									</p>
								</div>
								<Button
									type="button"
									variant="ghost"
									size="sm"
									onClick={(e) => {
										e.stopPropagation();
										updateFields({ pdfUrl: '' });
									}}
									className="ml-2 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
									title="Remove uploaded PDF"
								>
									<Trash2 className="size-4" />
								</Button>
							</div>
						) : (
							<div className="flex flex-col items-center gap-2">
								<div className="size-11 rounded-xl bg-white border border-slate-200 text-slate-700 flex items-center justify-center shadow-2xs">
									<FileUp className="size-5" />
								</div>
								<div>
									<span className="text-sm font-bold text-slate-900 hover:underline">
										Click to browse and upload PDF
									</span>
									<p className="text-[11px] text-slate-500 mt-0.5">
										Upload any document up to 1.8 MB directly
									</p>
								</div>
							</div>
						)}
					</div>
				</div>

				<div className="relative flex items-center justify-center my-2">
					<div className="border-t border-slate-200 w-full" />
					<span className="bg-white px-3 text-[11px] font-semibold text-slate-400 uppercase tracking-wider shrink-0">
						Or provide link
					</span>
				</div>

				{/* Online Link to PDF */}
				<div className="space-y-2">
					<Label htmlFor="pdfUrl" className="text-xs font-semibold text-slate-700">
						Option B: Direct PDF URL (HTTPS)
					</Label>
					<div className="flex gap-2">
						<Input
							id="pdfUrl"
							type="url"
							value={isUploadedPdf ? '' : (value.pdfUrl ?? '')}
							disabled={isUploadedPdf}
							onChange={(e) => updateFields({ pdfUrl: e.target.value })}
							placeholder={
								isUploadedPdf
									? 'Using directly uploaded PDF above'
									: 'https://example.com/files/brochure.pdf'
							}
							className="h-10 text-sm font-medium"
						/>
						<Button
							type="button"
							variant="outline"
							size="sm"
							onClick={testPdfLink}
							className="shrink-0 text-xs border-slate-300 hover:bg-slate-50 cursor-pointer"
						>
							<ExternalLink className="mr-1.5 size-3.5" />
							Test File
						</Button>
					</div>
					<p className="text-[11px] text-slate-500">
						Supported: Google Drive, Dropbox, AWS S3, Cloudflare, WordPress, or any hosted PDF link.
					</p>
				</div>
			</div>

			{/* Directly Show PDF Toggle Card - Positioned AFTER the uploader */}
			<div className="rounded-2xl border-2 border-slate-900/10 bg-slate-50/70 p-5 shadow-xs transition-all">
				<div className="flex items-center justify-between gap-4">
					<div className="flex items-start gap-3.5">
						<div className="flex size-10 items-center justify-center rounded-xl bg-slate-900 text-white shrink-0 shadow-xs">
							<Zap className="size-5" />
						</div>
						<div>
							<div className="flex items-center gap-2">
								<h2 className="text-base font-bold text-slate-950">Directly show PDF</h2>
								<span className="rounded-full bg-slate-200/80 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-700">
									Direct scan
								</span>
							</div>
							<p className="text-xs text-slate-600 mt-1 leading-normal">
								When enabled, scanning the QR code opens the PDF directly on the user's phone,
								bypassing the landing page.
							</p>
						</div>
					</div>
					<div className="shrink-0 flex items-center">
						<Switch
							id="directPdfSwitch"
							checked={isDirectPdf}
							onCheckedChange={(checked) => updateFields({ directPdf: checked })}
							aria-label="Directly show PDF"
						/>
					</div>
				</div>
			</div>

			{/* When "Directly show PDF" is toggled ON, the landing page form fields disappear */}
			{!isDirectPdf && (
				<>
					{/* Document Title & Information */}
					<div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 shadow-xs space-y-5 animate-in fade-in duration-200">
						<div className="flex items-center gap-3 border-b border-slate-100 pb-3">
							<div className="flex size-9 items-center justify-center rounded-xl bg-slate-100 text-slate-800">
								<FileText className="size-5" />
							</div>
							<div>
								<h2 className="text-base font-bold text-slate-900">Document Information</h2>
								<p className="text-xs text-slate-500">
									Give your PDF document a clear title, description, and publisher details
								</p>
							</div>
						</div>

						<div className="grid gap-4 sm:grid-cols-2">
							<div className="space-y-1.5 sm:col-span-2">
								<Label htmlFor="pdfTitle" className="text-xs font-semibold text-slate-700">
									Document Title
								</Label>
								<Input
									id="pdfTitle"
									value={value.title ?? ''}
									onChange={(e) => updateFields({ title: e.target.value })}
									placeholder="e.g. 2026 Product Catalogue & Pricing"
									className="h-10 text-sm font-medium"
								/>
							</div>

							<div className="space-y-1.5">
								<Label htmlFor="pdfCompany" className="text-xs font-semibold text-slate-700">
									Company / Author
								</Label>
								<Input
									id="pdfCompany"
									value={value.company ?? ''}
									onChange={(e) => updateFields({ company: e.target.value })}
									placeholder="e.g. Acme Corporation"
									className="h-10 text-sm"
								/>
							</div>

							<div className="space-y-1.5">
								<Label htmlFor="pdfSize" className="text-xs font-semibold text-slate-700">
									File Size (Display only)
								</Label>
								<Input
									id="pdfSize"
									value={value.fileSize ?? ''}
									onChange={(e) => updateFields({ fileSize: e.target.value })}
									placeholder="e.g. 3.4 MB"
									className="h-10 text-sm"
								/>
							</div>

							<div className="space-y-1.5 sm:col-span-2">
								<Label htmlFor="pdfDescription" className="text-xs font-semibold text-slate-700">
									Document Description / Overview
								</Label>
								<Textarea
									id="pdfDescription"
									value={value.description ?? ''}
									onChange={(e) => updateFields({ description: e.target.value })}
									placeholder="e.g. Complete brochure outlining our full collection of sustainable home and lifestyle products."
									rows={3}
									className="text-sm resize-none"
								/>
							</div>
						</div>

						{/* Cover Image & Theme Color */}
						<div className="pt-3 border-t border-slate-100 grid gap-4 sm:grid-cols-2">
							<div className="space-y-2">
								<Label className="text-xs font-semibold text-slate-700">
									Cover Image / Thumbnail
								</Label>
								<div className="flex items-center gap-3">
									{value.coverUrl ? (
										<div className="relative size-14 shrink-0 overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
											{/* eslint-disable-next-line @next/next/no-img-element */}
											<img
												src={value.coverUrl}
												alt="Cover preview"
												className="size-full object-cover"
											/>
											<button
												type="button"
												onClick={() => updateFields({ coverUrl: undefined })}
												className="absolute inset-0 flex items-center justify-center bg-black/50 text-white opacity-0 transition-opacity hover:opacity-100 cursor-pointer"
											>
												<Trash2 className="size-4" />
											</button>
										</div>
									) : (
										<button
											type="button"
											onClick={() => coverInputRef.current?.click()}
											className="flex size-14 shrink-0 flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50 text-slate-400 transition hover:border-slate-400 hover:text-slate-600 cursor-pointer"
										>
											<Upload className="size-5" />
										</button>
									)}
									<div className="text-xs text-slate-500">
										<button
											type="button"
											onClick={() => coverInputRef.current?.click()}
											className="font-medium text-slate-900 hover:underline cursor-pointer"
										>
											Upload cover thumbnail
										</button>
										<p>PNG or JPG up to 1 MB</p>
									</div>
									<input
										ref={coverInputRef}
										type="file"
										accept="image/png,image/jpeg,image/webp"
										onChange={handleCoverUpload}
										className="hidden"
									/>
								</div>
							</div>

							<div className="space-y-2">
								<Label className="text-xs font-semibold text-slate-700">Theme Color</Label>
								<div className="flex flex-wrap items-center gap-2">
									{COLOR_PALETTES.map((palette) => {
										const selected = (value.accentColor ?? '#1d4ed8') === palette.hex;
										return (
											<button
												key={palette.hex}
												type="button"
												onClick={() =>
													updateFields({
														accentColor: palette.hex,
														headerColor: palette.hex,
													})
												}
												className="relative size-8 rounded-full transition-transform hover:scale-110 cursor-pointer"
												style={{ backgroundColor: palette.hex }}
												title={palette.name}
											>
												{selected && (
													<Check className="absolute inset-0 m-auto size-4 text-white stroke-[3]" />
												)}
											</button>
										);
									})}
								</div>
							</div>
						</div>
					</div>

					{/* Contact & Organization Links */}
					<div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 shadow-xs space-y-4 animate-in fade-in duration-200">
						<div className="flex items-center gap-3 border-b border-slate-100 pb-3">
							<div className="flex size-9 items-center justify-center rounded-xl bg-slate-100 text-slate-700">
								<Building2 className="size-5" />
							</div>
							<div>
								<h2 className="text-base font-bold text-slate-900">Contact & Company Links</h2>
								<p className="text-xs text-slate-500">
									Give readers ways to contact your business after reviewing the document
								</p>
							</div>
						</div>

						<div className="grid gap-4 sm:grid-cols-3">
							<div className="space-y-1.5">
								<Label htmlFor="pdfWebsite" className="text-xs font-semibold text-slate-700">
									Website URL
								</Label>
								<Input
									id="pdfWebsite"
									type="url"
									value={value.website ?? ''}
									onChange={(e) => updateFields({ website: e.target.value })}
									placeholder="https://example.com"
									className="h-10 text-sm"
								/>
							</div>

							<div className="space-y-1.5">
								<Label htmlFor="pdfEmail" className="text-xs font-semibold text-slate-700">
									Contact Email
								</Label>
								<Input
									id="pdfEmail"
									type="email"
									value={value.email ?? ''}
									onChange={(e) => updateFields({ email: e.target.value })}
									placeholder="contact@example.com"
									className="h-10 text-sm"
								/>
							</div>

							<div className="space-y-1.5">
								<Label htmlFor="pdfPhone" className="text-xs font-semibold text-slate-700">
									Phone Number
								</Label>
								<Input
									id="pdfPhone"
									type="tel"
									value={value.phone ?? ''}
									onChange={(e) => updateFields({ phone: e.target.value })}
									placeholder="+1 (555) 000-1122"
									className="h-10 text-sm"
								/>
							</div>
						</div>
					</div>
				</>
			)}
		</div>
	);
}
